import { useEffect, useMemo, useRef, useState } from 'react'
import { ProductPicker } from '../components/ProductPicker'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { apiClient } from '@services/api'

type Unit = 'kg' | 'gms' | 'pkt' | 'Tin' | 'ltr'
type Paper = 'A4' | 'A5'
const UNITS: Unit[] = ['kg', 'gms', 'pkt', 'Tin', 'ltr']
interface PoLine { product: string; quantity: number; unit: string; rate: number; amount: number; received: number; remaining: number }
interface Po {
  id: number; poNumber: string; supplierName: string; supplier: { name: string; phone: string | null; address: string | null; location: string | null; gstin: string | null } | null
  orderDate: string; expectedDate: string | null; status: string; editable: boolean; lines: PoLine[]; orderedLabel: string; receivedLabel: string; value: number; overdueDays: number
  bills: Array<{ id: number; billNo: string; billDate: string; amount: number }>; createdAt: string
  company: { name: string; address?: string | null; city?: string | null; state?: string | null; pincode?: string | null; country?: string | null; gstin?: string | null; phone?: string | null; email?: string | null; logoUrl?: string | null } | null
}
interface EditLine { id: string; product: string; unit: Unit; qty: number; rate: number }

const money = (n: number) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const dmy = (iso: string) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const statusBadge: Record<string, string> = { Draft: 'badge-draft', Sent: 'badge-sent', 'Partially Received': 'badge-partial', 'Fully Received': 'badge-full', Cancelled: 'badge-draft', Closed: 'badge-ok' }

function PoSheet({ po, paper }: { po: Po; paper: Paper }) {
  const co = po.company
  const addr = co?.address ? co.address.split(',').map((p) => p.trim()).filter(Boolean) : []
  const addrLines = addr.length > 3 ? [addr.slice(0, 3).join(', '), addr.slice(3).join(', ')] : [addr.join(', ')]
  return (
    <div className={`inv-sheet inv-copy paper-${paper.toLowerCase()}`}>
      <div className="inv-head">
        <div className="inv-co">
          {co?.logoUrl && <img src={co.logoUrl} alt="" className="inv-logo" />}
          <div>
            <div className="inv-co-name">{co?.name}</div>
            {addrLines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
            {(co?.city || co?.pincode) && <div>{co?.city}{co?.pincode ? `- ${co.pincode}` : ''}</div>}
            {co?.state && <div>{co.state}</div>}
            {co?.gstin && <div>GSTIN: {co.gstin}</div>}
            {co?.phone && <div>Ph: {co.phone}</div>}
          </div>
        </div>
        <div className="inv-title">
          <div className="inv-title-main">Purchase Order</div>
          <div className="inv-title-no">PO # {po.poNumber}</div>
          <div className="inv-title-status">{po.status}</div>
        </div>
      </div>
      <table className="inv-box">
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div className="inv-kv"><span>Order Date</span><span>: {dmy(po.orderDate)}</span></div>
              {po.expectedDate && <div className="inv-kv"><span>Deliver by</span><span>: {dmy(po.expectedDate)}</span></div>}
              <div className="inv-kv"><span>Deliver to</span><span>: {co?.name}{co?.city ? `, ${co.city}` : ''}</span></div>
            </td>
            <td>
              <div>Supplier :</div>
              <div className="inv-bill-name">{po.supplier?.name || po.supplierName}</div>
              {po.supplier?.address && <div>{po.supplier.address}</div>}
              {!po.supplier?.address && po.supplier?.location && <div>{po.supplier.location}</div>}
              {po.supplier?.phone && <div>Ph: {po.supplier.phone}</div>}
              {po.supplier?.gstin && <div>GSTIN: {po.supplier.gstin}</div>}
            </td>
          </tr>
        </tbody>
      </table>
      <table className="inv-items">
        <thead><tr><th className="c-idx">#</th><th>Item</th><th className="num c-qty">Qty</th><th className="c-unit">Units</th><th className="num c-rate">Expected Rate</th><th className="num c-amt">Value</th></tr></thead>
        <tbody>
          {po.lines.map((l, i) => (
            <tr key={i}><td className="c-idx">{i + 1}</td><td>{l.product}</td><td className="num">{l.quantity.toFixed(2)}</td><td className="c-unit">{l.unit === 'pkt' ? 'pcs' : l.unit}</td><td className="num">{money(l.rate)}</td><td className="num">{money(l.amount)}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="inv-totals-wrap">
        <table className="inv-totals">
          <tbody>
            <tr><td>Items</td><td className="num">{po.lines.length}</td></tr>
            <tr><td>Quantity</td><td className="num">{po.orderedLabel}</td></tr>
            <tr className="inv-grand"><td>Expected Value</td><td className="num">₹{money(po.value)}</td></tr>
          </tbody>
        </table>
      </div>
      <table className="inv-foot">
        <tbody>
          <tr><td><span className="inv-lbl">Notes :</span> Rates are as expected at the time of order; the supplier's bill is final. Please mention PO # {po.poNumber} on the invoice and delivery challan.</td></tr>
          <tr><td><span className="inv-lbl">Contact :</span> {co?.phone || ''}{co?.email ? ` · ${co.email}` : ''}</td></tr>
        </tbody>
      </table>
    </div>
  )
}

export function PurchaseOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading, refetch } = useQuery({ queryKey: ['purchase-order', id], queryFn: () => apiClient.get<any>(`/purchase-orders/${id}`), enabled: !!id })
  const po: Po | undefined = data?.data
  const { data: suppliersData } = useQuery({ queryKey: ['suppliers-list'], queryFn: () => apiClient.get<any>('/suppliers') })
  const { data: productsData } = useQuery({ queryKey: ['products-list'], queryFn: () => apiClient.get<any>('/products') })
  const suppliers: Array<{ id: number; name: string }> = suppliersData?.data?.items || []
  const products: Array<{ id: number; name: string; unit: Unit; nameTamil?: string | null; category?: string | null }> = productsData?.data?.items || []
  const pickerOptions = useMemo(() => products.map((p) => ({ value: p.name, label: p.name, sub: p.nameTamil || null, right: `per ${p.unit}`, rightSub: p.category || null })), [products])

  const [paper, setPaper] = useState<Paper>('A4')
  const [exporting, setExporting] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [supplierId, setSupplierId] = useState('')
  const [orderDate, setOrderDate] = useState('')
  const [expectedDate, setExpectedDate] = useState('')
  const [lines, setLines] = useState<EditLine[]>([])

  useEffect(() => {
    if (!po) return
    setSupplierId(String(po.supplier ? (po as any).supplierId || '' : ''))
    setOrderDate(po.orderDate); setExpectedDate(po.expectedDate || '')
    setLines(po.lines.map((l, i) => ({ id: String(i + 1), product: l.product, unit: (l.unit as Unit) || 'kg', qty: l.quantity, rate: l.rate })))
  }, [po])

  const act = async (fn: () => Promise<any>, after?: () => void) => {
    setNotice(null); setBusy(true)
    try { const res = await fn(); setNotice({ kind: 'success', text: res.message }); await refetch(); queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }); after?.() }
    catch (err: any) { setNotice({ kind: 'error', text: err.response?.data?.message || 'Action failed' }) }
    finally { setBusy(false) }
  }
  const saveEdit = () => {
    const real = lines.filter((l) => l.product.trim())
    if (real.length === 0) return setNotice({ kind: 'error', text: 'Add at least one line item' })
    act(() => apiClient.patch<any>(`/purchase-orders/${id}`, { supplierId: supplierId ? Number(supplierId) : undefined, orderDate, expectedDate: expectedDate || null, lines: real.map((l) => ({ product: l.product.trim(), quantity: l.qty, rate: l.rate, unit: l.unit })) }), () => setEditing(false))
  }
  const catalogueUnit = (name: string) => products.find((p) => p.name.toLowerCase() === name.trim().toLowerCase())?.unit
  const downloadPdf = async () => {
    if (!po || !printRef.current) return
    setExporting(true)
    await new Promise((r) => setTimeout(r, 50))
    try {
      const { default: html2pdf } = await import('html2pdf.js')
      await html2pdf().set({ margin: paper === 'A5' ? [6, 6, 6, 6] : [10, 10, 10, 10], filename: `${po.poNumber}.pdf`, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' }, jsPDF: { unit: 'mm', format: paper.toLowerCase(), orientation: 'portrait' } }).from(printRef.current).save()
    } finally { setExporting(false) }
  }

  if (isLoading || !po) return <div className="hdr"><h1>Purchase Order</h1><p>{isLoading ? 'Loading…' : 'Purchase order not found.'}</p></div>
  const editTotal = lines.reduce((s, l) => s + (l.qty || 0) * (l.rate || 0), 0)

  return (
    <div className="inv-page">
      <style>{`@page { size: ${paper} portrait; margin: 0; }`}</style>
      <div className="hdr print:hidden">
        <div className="field-label-row" style={{ alignItems: 'flex-start' }}>
          <div>
            <h1>{po.poNumber}</h1>
            <p><Link to="/dashboard/procurement/purchase-orders" className="text-primary-500 hover:underline">Purchase Orders</Link> › {po.poNumber} · {po.supplierName} · ordered {formatDate(po.orderDate)}{po.expectedDate ? ` · expected ${formatDate(po.expectedDate)}` : ''}{po.overdueDays > 0 ? ` · ${po.overdueDays}d overdue` : ''}</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-end">
            <span className={`badge ${statusBadge[po.status] || 'badge-draft'}`}>{po.status}</span>
            <select value={paper} onChange={(e) => setPaper(e.target.value as Paper)} style={{ width: 'auto', marginBottom: 0, padding: '6px 8px', fontSize: 12 }} aria-label="Paper size"><option value="A4">A4</option><option value="A5">A5</option></select>
            <button type="button" className="btn-ghost" onClick={downloadPdf} disabled={exporting}>{exporting ? 'Preparing PDF…' : 'Download PDF'}</button>
            <button type="button" className="btn-ghost" onClick={() => window.print()}>Print {paper}</button>
            {po.editable && !editing && <button type="button" className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>}
            {po.status === 'Draft' && <button type="button" className="btn-primary" disabled={busy} onClick={() => act(() => apiClient.post<any>(`/purchase-orders/${id}/send`, {}))}>Send to supplier</button>}
            {(po.status === 'Sent' || po.status === 'Partially Received') && <button type="button" className="btn-accent" onClick={() => navigate(`/dashboard/procurement/purchase-bills?po=${po.id}`)}>Record purchase bill →</button>}
          </div>
        </div>
      </div>

      {notice && <div className={`alert ${notice.kind === 'success' ? 'alert-success' : 'alert-error'} print:hidden`}>{notice.text}</div>}

      {editing && (
        <div className="card print:hidden" style={{ background: 'var(--primary-soft)' }}>
          <h2>Edit {po.poNumber}</h2>
          <div className="grid grid-cols-3 gap-3">
            <div><label>Supplier</label><select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}><option value="">— keep current —</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div><label>Order Date</label><input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} /></div>
            <div><label>Expected Delivery</label><input type="date" value={expectedDate} min={orderDate} onChange={(e) => setExpectedDate(e.target.value)} /></div>
          </div>
          <table>
            <thead><tr><th>Product</th><th className="num" style={{ width: 120 }}>Qty</th><th style={{ width: 110 }}>UOM</th><th className="num" style={{ width: 150 }}>Expected Rate</th><th className="num" style={{ width: 110 }}>Value</th><th className="w-8"></th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td><ProductPicker options={pickerOptions} text={l.product} onText={(t) => setLines((ls) => ls.map((x) => (x.id === l.id ? { ...x, product: t, unit: catalogueUnit(t) || x.unit } : x)))} onSelect={(o) => setLines((ls) => ls.map((x) => (x.id === l.id ? { ...x, product: o.value, unit: catalogueUnit(o.value) || x.unit } : x)))} placeholder="Type or pick a product" className="!py-[6px]" inputProps={{ style: { marginBottom: 0 } }} /></td>
                  <td className="num"><input type="number" min={0} step={l.unit === 'kg' ? '0.001' : '1'} value={Number.isNaN(l.qty) ? '' : l.qty} onChange={(e) => setLines((ls) => ls.map((x) => (x.id === l.id ? { ...x, qty: e.target.value === '' ? NaN : Number(e.target.value) } : x)))} className="!w-20 !py-[6px] text-right" style={{ marginBottom: 0 }} /></td>
                  <td><select value={l.unit} onChange={(e) => setLines((ls) => ls.map((x) => (x.id === l.id ? { ...x, unit: e.target.value as Unit } : x)))} className="!w-24 !py-[6px] !px-[6px] !text-xs" style={{ marginBottom: 0 }}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select></td>
                  <td className="num"><input type="number" min={0} step="0.01" value={Number.isNaN(l.rate) ? '' : l.rate} onChange={(e) => setLines((ls) => ls.map((x) => (x.id === l.id ? { ...x, rate: e.target.value === '' ? NaN : Number(e.target.value) } : x)))} className="!w-24 !py-[6px] text-right" style={{ marginBottom: 0 }} /></td>
                  <td className="num font-semibold">{formatINR((l.qty || 0) * (l.rate || 0))}</td>
                  <td className="text-center"><button type="button" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.id !== l.id) : ls))} disabled={lines.length === 1} className="text-ink-soft hover:text-danger disabled:opacity-30 text-base leading-none">×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn-ghost mt-[10px]" onClick={() => setLines((ls) => [...ls, { id: String(Math.max(0, ...ls.map((x) => parseInt(x.id))) + 1), product: '', unit: 'kg', qty: NaN, rate: NaN }])}>+ Add another item</button>
          <div className="hint">Expected value {formatINR(editTotal)}.</div>
          <div className="btnrow">
            <button type="button" className="btn-primary" disabled={busy} onClick={saveEdit}>{busy ? 'Saving…' : 'Save changes'}</button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div ref={printRef} className={`inv-print-area ${exporting ? 'inv-exporting' : ''}`}>
        <div className={`inv-copy-wrap ${paper === 'A5' ? 'a5' : 'a4'}`}><PoSheet po={po} paper={paper} /></div>
      </div>

      <div className="grid2 mt-[18px] print:hidden">
        <div className="card">
          <h2>Receipt status</h2>
          <table>
            <thead><tr><th>Product</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Remaining</th></tr></thead>
            <tbody>
              {po.lines.map((l) => (
                <tr key={l.product}><td>{l.product}</td><td className="num">{l.quantity.toLocaleString('en-IN')} {l.unit}</td><td className="num">{po.status === 'Draft' ? '—' : `${l.received.toLocaleString('en-IN')} ${l.unit}`}</td><td className={`num ${l.remaining > 0 && po.status !== 'Draft' ? 'text-accent-500 font-semibold' : ''}`}>{po.status === 'Draft' ? '—' : `${l.remaining.toLocaleString('en-IN')} ${l.unit}`}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="hint">Received quantities come from purchase bills linked to this PO{po.bills.length ? `: ${po.bills.map((b) => b.billNo).join(', ')}` : ' — none yet'}.</div>
          {po.bills.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{po.bills.map((b) => <Link key={b.id} to={`/dashboard/procurement/purchase-bills/${b.id}`} className="btn-ghost btn-xs">{b.billNo} · {formatDate(b.billDate)} · {formatINR(b.amount)} →</Link>)}</div>}
        </div>
        <div className="card">
          <h2>Actions</h2>
          <div className="flex gap-2 flex-wrap">
            {po.status === 'Draft' && <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={() => act(() => apiClient.delete<any>(`/purchase-orders/${id}`), () => navigate('/dashboard/procurement/purchase-orders'))}>Delete draft</button>}
            {(po.status === 'Sent' || po.status === 'Partially Received') && <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={() => act(() => apiClient.post<any>(`/purchase-orders/${id}/close`, {}))}>Close (stop expecting)</button>}
            {(po.status === 'Sent' || po.status === 'Partially Received') && <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={() => { if (window.confirm(`Cancel ${po.poNumber}?`)) act(() => apiClient.post<any>(`/purchase-orders/${id}/cancel`, {})) }}>Cancel PO</button>}
          </div>
          <div className="hint" style={{ marginTop: 10 }}>{po.editable ? 'This order can still be edited — nothing has been received against it yet.' : `Editing is locked because the order is ${po.status.toLowerCase()}.`}</div>
        </div>
      </div>
    </div>
  )
}
